import { useState, useEffect } from 'react';
import { Card } from '@medikei/ui';
import { Button } from '@medikei/ui';
import { Loader2, Clock, Users, CheckCircle } from 'lucide-react';

interface WaitingQueueProps {
  doctorName: string;
  specialty: string;
  estimatedWait: number;
  queuePosition?: number;
  doctorsOnline?: number;
  isDoctorReady?: boolean;
  onCancel?: () => void;
  onJoinQueue?: () => void;
  onEnterConsultation?: () => void;
}

export function WaitingQueue({
  doctorName,
  specialty,
  estimatedWait,
  queuePosition: initialPosition,
  doctorsOnline: initialDoctorsOnline,
  isDoctorReady = false,
  onCancel,
  onEnterConsultation,
}: WaitingQueueProps) {
  const [queuePosition, setQueuePosition] = useState(initialPosition || 3);
  const [currentWait, setCurrentWait] = useState(0);
  const [dots, setDots] = useState(1);
  const [doctorsOnline, setDoctorsOnline] = useState(initialDoctorsOnline || 0);

  useEffect(() => {
    if (initialPosition !== undefined) {
      setQueuePosition(initialPosition);
    }
  }, [initialPosition]);

  useEffect(() => {
    if (initialDoctorsOnline !== undefined) {
      setDoctorsOnline(initialDoctorsOnline);
    }
  }, [initialDoctorsOnline]);

  useEffect(() => {
    const waitInterval = setInterval(() => {
      setCurrentWait((prev) => prev + 1);
    }, 60000);

    return () => clearInterval(waitInterval);
  }, []);

  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 500);

    return () => clearInterval(dotsInterval);
  }, []);

  const progress = isDoctorReady ? 100 : queuePosition > 0 ? Math.max(10, 100 - (queuePosition * 25)) : 10;

  return (
    <Card className="overflow-hidden rounded-2xl sm:rounded-3xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
      <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-4 sm:p-6 border-b">
        <div className="flex items-center gap-3.5 sm:gap-4">
          <div className="relative">
            {isDoctorReady ? (
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
              </div>
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 text-primary animate-spin" />
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-primary rounded-full flex items-center justify-center border-2 border-white">
              <Users className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary-foreground" />
            </div>
          </div>
          <div>
            <h3 className="font-bold text-base sm:text-lg">
              {isDoctorReady ? 'Médico Disponível!' : 'Aguardando na Fila'}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {isDoctorReady
                ? `${doctorName} está pronto para atendê-lo`
                : `${doctorName} - ${specialty}`}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="mb-5 sm:mb-6">
          <div className="flex justify-between text-xs sm:text-sm mb-2">
            <span className="text-muted-foreground">Progresso da fila</span>
            <span className="font-semibold">{progress}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5 sm:gap-4 mb-5 sm:mb-6">
          <div className="bg-muted/50 rounded-xl p-3 sm:p-4 text-center">
            <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-1">
              <Users className="h-3.5 w-3.5" />
              <span className="text-[11px] sm:text-xs">Posição</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-primary">{queuePosition}</p>
          </div>

          <div className="bg-muted/50 rounded-xl p-3 sm:p-4 text-center">
            <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-1">
              <Clock className="h-3.5 w-3.5" />
              <span className="text-[11px] sm:text-xs">Aguardando</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-foreground">
              {currentWait}
              <span className="text-xs sm:text-sm font-normal ml-1">min</span>
            </p>
          </div>
        </div>

        {!isDoctorReady && (
          <div className="bg-muted/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex gap-1">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      i <= dots ? 'bg-primary' : 'bg-muted'
                    }`}
                    style={{
                      animation: i <= dots ? 'pulse 1s ease-in-out infinite' : 'none',
                    }}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                Procurando médicos disponíveis
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Tempo médio de espera: ~{estimatedWait} minutos
            </p>
          </div>
        )}

        {isDoctorReady && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-800">Consulta disponível</span>
            </div>
            <p className="text-sm text-green-700">
              O médico está pronto para iniciar sua consulta agora mesmo.
            </p>
          </div>
        )}

        <div className="space-y-2.5 sm:space-y-3">
          {isDoctorReady ? (
            <Button
              className="w-full h-11 sm:h-12 text-sm sm:text-base font-bold shadow-md cursor-pointer rounded-xl"
              size="lg"
              onClick={() => {
                console.log('[WAITING-QUEUE] Clique em Entrar na Consulta detectado!', {
                  onEnterConsultation: Boolean(onEnterConsultation),
                  doctorName,
                  isDoctorReady,
                });
                if (onEnterConsultation) {
                  onEnterConsultation();
                } else {
                  console.warn('[WAITING-QUEUE] onEnterConsultation callback não foi fornecido.');
                }
              }}
            >
              <CheckCircle className="h-5 w-5 mr-2" />
              Entrar na Consulta
            </Button>
          ) : (
            <Button className="w-full h-11 sm:h-12 text-sm sm:text-base font-bold rounded-xl" disabled>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Aguardando...
            </Button>
          )}

          <Button variant="outline" className="w-full h-10 sm:h-11 text-xs sm:text-sm font-semibold rounded-xl" onClick={onCancel}>
            Sair da Fila
          </Button>
        </div>

        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>Médicos online agora: {doctorsOnline}</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </Card>
  );
}
